/*
 * Artink "Watch & buy" reels.
 * - Carousel: arrows step through a native scroll-snap track.
 * - Cards: muted previews play only while on screen (and never with reduced motion).
 * - Player: a <dialog> story player with progress segments, prev/next (buttons, arrow keys,
 *   swipe), tap to pause, mute, share, optional WhatsApp, and a product sheet with add to cart.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function readReels(root) {
  try {
    return JSON.parse(root.querySelector('[data-reels-json]')?.textContent || '[]');
  } catch {
    return [];
  }
}

function bootCarousel(root, signal) {
  const track = root.querySelector('[data-reels-track]');
  const prev = root.querySelector('[data-reels-prev]');
  const next = root.querySelector('[data-reels-next]');
  if (!track) return;

  const isRtl = () => getComputedStyle(track).direction === 'rtl';
  const update = () => {
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const pos = Math.abs(track.scrollLeft);
    if (prev) prev.disabled = pos <= 2;
    if (next) next.disabled = pos >= max - 2;
    root.classList.toggle('is-static', max <= 2);
  };
  const step = (dir) => {
    const slide = track.querySelector('.artink-reels__slide');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const w = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const perView = Math.max(1, Math.floor((track.clientWidth + gap) / w));
    track.scrollBy({ left: w * perView * dir * (isRtl() ? -1 : 1), behavior: reduceMotion() ? 'auto' : 'smooth' });
  };

  prev?.addEventListener('click', () => step(-1), { signal });
  next?.addEventListener('click', () => step(1), { signal });
  track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true, signal });
  window.addEventListener('resize', update, { signal });
  update();
}

/* Muted card previews play while visible; poster stays until the first frame is ready. */
function bootPreviews(root, signal) {
  const videos = [...root.querySelectorAll('[data-reel-preview]')];
  if (!videos.length || root.dataset.autoplayPreviews === 'false' || reduceMotion() || !window.IntersectionObserver) {
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting && !root.classList.contains('is-watching')) {
          video.play().then(
            () => video.classList.add('is-playing'),
            () => {}
          );
        } else {
          video.pause();
        }
      }
    },
    { threshold: 0.6 }
  );
  videos.forEach((video) => io.observe(video));
  signal.addEventListener('abort', () => io.disconnect());
  return {
    pauseAll: () => videos.forEach((v) => v.pause()),
    resume: () => {
      // Re-observing re-fires the visibility check.
      videos.forEach((v) => {
        io.unobserve(v);
        io.observe(v);
      });
    },
  };
}

function bootPlayer(root, reels, previews, signal) {
  const dialog = root.querySelector('[data-reels-modal]');
  if (!dialog || !reels.length) return;

  const video = dialog.querySelector('[data-reels-video]');
  const still = dialog.querySelector('[data-reels-still]');
  const frame = dialog.querySelector('[data-reels-frame]');
  const bars = dialog.querySelector('[data-reels-bars]');
  const paused = dialog.querySelector('[data-reels-paused]');
  const playBtn = dialog.querySelector('[data-reels-play]');
  const muteBtn = dialog.querySelector('[data-reels-mute]');
  const shareBtn = dialog.querySelector('[data-reels-share]');
  const waBtn = dialog.querySelector('[data-reels-whatsapp]');
  const cap = dialog.querySelector('[data-reels-cap]');
  const buy = dialog.querySelector('[data-reels-buy]');
  const buyImg = dialog.querySelector('[data-reels-buy-img]');
  const buyTitle = dialog.querySelector('[data-reels-buy-title]');
  const buyPrice = dialog.querySelector('[data-reels-buy-price]');
  const buyCompare = dialog.querySelector('[data-reels-buy-compare]');
  const addBtn = dialog.querySelector('[data-reels-add]');
  const viewLink = dialog.querySelector('[data-reels-view]');
  const status = dialog.querySelector('[data-reels-status]');
  const steps = [...dialog.querySelectorAll('[data-reels-step]')];
  const labels = dialog.dataset;
  const autoAdvance = root.dataset.autoAdvance !== 'false';

  const STILL_MS = 6000;

  let index = 0;
  let muted = true;
  let opener = null;
  let raf = 0;
  let addAbort = null;
  // Photo reels (no video) run on a timer instead of a video clock.
  let stillMode = false;
  let stillStart = 0;
  let stillElapsed = 0;
  let stillPaused = false;
  if (still) still.style.setProperty('--reels-still-ms', `${STILL_MS}ms`);

  // One progress segment per reel.
  bars.replaceChildren(
    ...reels.map(() => {
      const seg = document.createElement('div');
      seg.className = 'artink-reels__seg';
      seg.append(document.createElement('span'));
      return seg;
    })
  );
  const segs = [...bars.children];

  const stillProgress = () => {
    const elapsed = stillPaused ? stillElapsed : performance.now() - stillStart;
    return Math.min(1, elapsed / STILL_MS);
  };

  const paintProgress = () => {
    let current = 0;
    if (stillMode) current = stillProgress();
    else if (video.duration) current = video.currentTime / video.duration;
    segs.forEach((seg, i) => {
      seg.firstElementChild.style.setProperty('--p', i < index ? 1 : i === index ? current : 0);
    });
  };

  // Shared by videos ('ended') and photo reels (timer): next reel, or stop on the last.
  let onFinished = () => {};

  const tick = () => {
    paintProgress();
    if (stillMode && !stillPaused && stillProgress() >= 1) onFinished();
    raf = requestAnimationFrame(tick);
  };

  const announce = (text) => {
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => (status.textContent = text), 50);
  };

  const setPlaying = (playing) => {
    paused.hidden = playing;
    playBtn?.classList.toggle('is-paused', !playing);
    playBtn?.setAttribute('aria-label', playing ? playBtn.dataset.labelPause : playBtn.dataset.labelPlay);
  };

  const play = () => {
    if (stillMode) {
      stillStart = performance.now() - stillElapsed;
      stillPaused = false;
      still?.classList.remove('is-paused');
      setPlaying(true);
      return;
    }
    video.play().then(
      () => setPlaying(true),
      () => setPlaying(false)
    );
  };

  const pause = () => {
    if (stillMode) {
      stillElapsed = performance.now() - stillStart;
      stillPaused = true;
      still?.classList.add('is-paused');
    } else {
      video.pause();
    }
    setPlaying(false);
  };

  const isPaused = () => (stillMode ? stillPaused : video.paused);
  const togglePlay = () => (isPaused() ? play() : pause());

  const renderProduct = (reel) => {
    addAbort?.abort();
    const product = reel.product;
    buy.hidden = !product;
    if (!product) return;
    buyImg.src = product.image || 'data:,';
    buyTitle.textContent = reel.title || product.title;
    buyPrice.textContent = product.price || '';
    buyCompare.textContent = product.compare || '';
    viewLink.href = product.url;
    addBtn.removeAttribute('aria-busy');
    if (!product.available) {
      addBtn.textContent = labels.soldLabel;
      addBtn.disabled = true;
      addBtn.dataset.mode = 'sold';
    } else if (!product.simple) {
      addBtn.textContent = labels.optionsLabel;
      addBtn.disabled = false;
      addBtn.dataset.mode = 'options';
    } else {
      addBtn.textContent = labels.addLabel;
      addBtn.disabled = false;
      addBtn.dataset.mode = 'add';
    }
  };

  const show = (i) => {
    index = (i + reels.length) % reels.length;
    const reel = reels[index];
    video.pause();
    stillMode = !reel.src;

    if (stillMode) {
      video.removeAttribute('src');
      video.load();
      video.hidden = true;
      if (still) {
        still.src = reel.poster || 'data:,';
        still.alt = reel.title || '';
        still.hidden = false;
        // Restart the slow drift for this photo.
        still.classList.remove('is-on', 'is-paused');
        void still.offsetWidth;
        still.classList.add('is-on');
      }
      stillElapsed = 0;
      stillPaused = false;
      stillStart = performance.now();
    } else {
      if (still) {
        still.hidden = true;
        still.classList.remove('is-on', 'is-paused');
      }
      video.hidden = false;
      video.muted = muted;
      video.loop = !autoAdvance || reels.length === 1;
      video.poster = reel.poster || '';
      video.src = reel.src;
    }

    if (muteBtn) muteBtn.hidden = stillMode;
    cap.textContent = reel.caption || '';
    cap.hidden = !reel.caption;
    renderProduct(reel);
    steps.forEach((btn) => (btn.disabled = reels.length < 2));
    paintProgress();
    play();
  };

  onFinished = () => {
    if (autoAdvance && index < reels.length - 1) show(index + 1);
    else if (!autoAdvance && stillMode) show(index);
    else {
      pause();
      if (stillMode) stillElapsed = STILL_MS;
    }
  };

  const open = (i, from) => {
    opener = from || null;
    root.classList.add('is-watching');
    previews?.pauseAll?.();
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.showModal();
    show(i);
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(tick);
  };

  const close = () => {
    if (dialog.open) dialog.close();
  };

  dialog.addEventListener(
    'close',
    () => {
      cancelAnimationFrame(raf);
      addAbort?.abort();
      video.pause();
      video.removeAttribute('src');
      video.load();
      stillMode = false;
      still?.classList.remove('is-on', 'is-paused');
      document.documentElement.removeAttribute('scroll-lock');
      root.classList.remove('is-watching');
      previews?.resume?.();
      // Return focus to the card that opened the player.
      opener?.focus?.();
    },
    { signal }
  );

  root.addEventListener(
    'click',
    (event) => {
      const card = event.target instanceof Element ? event.target.closest('[data-reel-open]') : null;
      if (!card || card.disabled) return;
      open(Number(card.dataset.reelOpen) || 0, card);
    },
    { signal }
  );

  dialog.querySelector('[data-reels-close]')?.addEventListener('click', close, { signal });
  steps.forEach((btn) =>
    btn.addEventListener('click', () => show(index + Number(btn.dataset.reelsStep)), { signal })
  );

  // Clicking the dark backdrop (outside the stage controls) closes the player.
  dialog.addEventListener(
    'click',
    (event) => {
      if (event.target === dialog || event.target.matches?.('[data-reels-stage]')) close();
    },
    { signal }
  );

  dialog.addEventListener(
    'keydown',
    (event) => {
      if (event.target instanceof Element && event.target.closest('input, textarea, select')) return;
      const rtl = getComputedStyle(dialog).direction === 'rtl';
      if (event.key === 'ArrowRight') show(index + (rtl ? -1 : 1));
      else if (event.key === 'ArrowLeft') show(index + (rtl ? 1 : -1));
      else if (event.key === ' ' && (event.target === video || event.target === still)) {
        event.preventDefault();
        togglePlay();
      }
    },
    { signal }
  );

  // Tap the video or photo to pause / resume.
  video.addEventListener('click', togglePlay, { signal });
  still?.addEventListener('click', togglePlay, { signal });
  playBtn?.addEventListener('click', togglePlay, { signal });
  video.addEventListener('ended', () => onFinished(), { signal });

  muteBtn?.addEventListener(
    'click',
    () => {
      muted = !muted;
      video.muted = muted;
      muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      if (!muted && !stillMode && video.paused) play();
    },
    { signal }
  );

  // Swipe left/right on phones to change reel.
  let sx = 0;
  let sy = 0;
  frame.addEventListener(
    'touchstart',
    (e) => {
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
    },
    { passive: true, signal }
  );
  frame.addEventListener(
    'touchend',
    (e) => {
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) {
        // A strong downward swipe closes, like most story players.
        if (dy > 110 && Math.abs(dx) < 60) close();
        return;
      }
      const rtl = getComputedStyle(dialog).direction === 'rtl';
      show(index + ((dx < 0) !== rtl ? 1 : -1));
    },
    { passive: true, signal }
  );

  shareBtn?.addEventListener(
    'click',
    async () => {
      const reel = reels[index];
      const url = reel.product?.absoluteUrl || window.location.href;
      const title = reel.title || document.title;
      try {
        if (navigator.share) {
          await navigator.share({ title, url });
          return;
        }
        await navigator.clipboard.writeText(url);
        announce(labels.copiedLabel);
        shareBtn.classList.add('is-done');
        window.setTimeout(() => shareBtn.classList.remove('is-done'), 1500);
      } catch {
        /* share sheet dismissed */
      }
    },
    { signal }
  );

  waBtn?.addEventListener(
    'click',
    () => {
      const base = root.dataset.whatsapp;
      if (!base) return;
      const reel = reels[index];
      const text = [root.dataset.whatsappText, reel.title, reel.product?.absoluteUrl].filter(Boolean).join(' ');
      window.open(`${base}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    },
    { signal }
  );

  addBtn.addEventListener(
    'click',
    async () => {
      const product = reels[index]?.product;
      if (!product || addBtn.getAttribute('aria-busy') === 'true') return;
      if (addBtn.dataset.mode === 'options') {
        window.location.href = product.url;
        return;
      }
      if (addBtn.dataset.mode !== 'add') return;

      addAbort?.abort();
      addAbort = new AbortController();
      const forIndex = index;
      addBtn.setAttribute('aria-busy', 'true');
      try {
        const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(product.variantId), quantity: 1 }] }),
          signal: addAbort.signal,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || data.message || labels.errorLabel);
        }
        await window.ArtinkCart?.refresh?.().catch(() => {});
        if (forIndex !== index) return;
        addBtn.textContent = `${labels.addedLabel} ✓`;
        announce(labels.addedLabel);
        window.setTimeout(() => {
          if (forIndex === index && dialog.open) addBtn.textContent = labels.addLabel;
        }, 2200);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        announce(error?.message || labels.errorLabel);
        addBtn.textContent = labels.addLabel;
      } finally {
        addBtn.removeAttribute('aria-busy');
      }
    },
    { signal }
  );

  signal.addEventListener('abort', () => {
    cancelAnimationFrame(raf);
    if (dialog.open) dialog.close();
  });
}

function bootReels(root) {
  if (!root || root.dataset.reelsBooted === '1') return;
  root.dataset.reelsBooted = '1';
  const controller = new AbortController();
  const reels = readReels(root);
  bootCarousel(root, controller.signal);
  const previews = bootPreviews(root, controller.signal);
  bootPlayer(root, reels, previews, controller.signal);
  root._artinkReelsCleanup = () => {
    controller.abort();
    root.dataset.reelsBooted = '0';
  };
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-artink-reels]').forEach(bootReels);
}

initAll();
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-reels]').forEach((root) => root._artinkReelsCleanup?.());
});
